import axios from "axios";
import { ChartData } from "chart.js";
import Vue from "vue";
import {
  COLLECTIONS_NAME,
  ENDPOINTS,
  ENDPOINT_OPTIONS,
  FORM_MESSAGES,
  LOCAL_STORAGE
} from "../constants/FormConstants";
import { ArrayByKeys } from "../models/ArrayByKeys";
import XLSX from "xlsx";
import { TableHeader } from "@/modules/Dashboards/AdminDashboard/models/TableHeader";
import { TableField } from "@/modules/Dashboards/AdminDashboard/models/TableField";
import {
  ACCESS_OPTIONS_TRUE,
  COLORS_PALETTE,
  EXCEL_CONSTANTS,
  getKeyValue,
  KEYTYPE_CONSTANTS,
  OBJECT,
  PANEL_INFO_CONSTANTS,
  ROUTES,
  STORE_CONSTANTS,
  METHODS,
  GENERAL_CONSTANTS,
  ALPHANUMERIC_CHARACTERS
} from "../constants/GeneralConstants";
import { ChartRenderData } from "@/modules/Dashboards/AdminDashboard/models/ChartRenderData";
import { CrudChartData, CrudChartOneData } from "../models/CrudChartData";
import { ListHeader } from "@/modules/List/models/ListHeader";
import { DynamicSelectData } from "../components/DynamicSelect/model/DynamicSelectData";
import { NAME_CLASS } from "./../constants/SelectConstants";
import { AccessOptions } from "../models/AccessOptions";
import { ModalFormData } from "@/modules/Home/components/ContextPanel/models/ModalFormData";
import { LIST_DATA_EVENTS } from "@/shared/constants/FormConstants";
import { DashboardCard } from "@/modules/Dashboards/models/DashboardCard";
import { RouteMapData } from "../components/RouteMap/model/RouteMap";
import { FormsData } from "@/modules/Forms/models/FormsData";
import { FORMS_CONSTANTS } from "@/modules/Forms/constants/FormsConstants";
import { RouteLocation } from "./../models/RouteLocation";
import store from "@/store";
import { ListService } from "@/modules/List/services/ListService";
import {
  SecondaryFilter,
  SecondaryFilterOptions
} from "@/modules/Dashboards/AdminDashboard/models/SecondaryFilter";
import { DEFAULT_CATEGORY_SERVICE_OPTION } from "@/modules/Services/constants/ServicesConstants";
import { Http } from "@status/codes";
import { FieldSchema } from "../models/FieldSchema";
import { FormSchema } from "../models/FormSchema";
import { FILE } from "../constants/FileConstants";
import { AllObjFilterList } from "../components/GenericSearchBar/model/List";
import { FILTER_DISPLAY } from "@/modules/Dashboards/AdminDashboard/constants/AdminDashboardConstants";
import { AxiosError } from "axios";
import { ActionPlanService } from "@/modules/ActionPlan/services/ActionPlanService";
import { CampusService } from "@/modules/Campus/services/CampusService";
import { CupsPriceLevelService } from "@/modules/CupsPriceLevels/services/CupsPriceLevelService";
import { DependenciesService } from "@/modules/Dependencies/services/DependenciesService";
import { InfoGroupService } from "@/modules/InfoGroup/services/InfoGroupService";
import { FormTemplateService } from "@/modules/FormTemplate/services/FormTemplateService";
import { RolesService } from "@/modules/Roles/services/RolesService";
import { ServiceTemplateService } from "@/modules/ServiceTemplate/services/ServiceTemplateService";
import { SessionTemplateService } from "@/modules/SessionTemplate/services/SessionTemplateService";
import { SpaceService } from "@/modules/Space/services/SpaceService";
import { TemplatePdfService } from "@/modules/TemplatePdf/services/templatePdfService";
import { UserTemplateService } from "@/modules/UserTemplate/services/UserTemplateService";
import { MultimediaService } from "@/modules/Multimedia/services/MultimediaService";
import { GeneralRulesService } from "@/modules/GeneralRules/services/GeneralRulesService";
import Snackbar from "../components/Snackbar/Snackbar";
import { DASHBOARD_ACCESS } from "@/modules/Dashboards/AdminDashboard/constants/ActionConstants";
import { DocumentService } from "@/modules/Document/services/DocumentService";
import { DocumentDTO } from "@/modules/Document/models/DocumentDTO";
import tinycolor from "tinycolor2";

/** Fragmento de schema JSON con properties / allOf (campos de formulario). */
export type JsonSchemaFragmentLike = {
  type?: string;
  properties?: Record<string, unknown>;
  allOf?: Array<{ properties?: Record<string, unknown> }>;
};

export type SplitFormFieldsByHtmlResult = {
  htmlFields: Record<string, unknown>;
  vjsfFields: Record<string, unknown>;
};

export type SplitFormSchemaByHtmlResult = {
  htmlSchema: JsonSchemaFragmentLike;
  vjsfSchema: JsonSchemaFragmentLike;
};

export class Utils {
  static countryIpUrl = ENDPOINTS.COUNTRY;

  static urlBase64ToUint8Array(base64String: string) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  static async getIpCountry(): Promise<string | void> {
    const response = await axios.get(this.countryIpUrl);

    if (response.data) {
      return response.data.country_code;
    } else {
      return undefined;
    }
  }

  static getArrayByKeys<T extends object>(object: T, att: string[]) {
    const keys = Object.keys(object);

    return keys.map(k => {
      const r: ArrayByKeys = { key: k };
      const obj = object[k as keyof typeof object];

      att.forEach((a: string) => {
        Vue.set(r, a, obj[a as keyof typeof obj]);
      });

      return r;
    });
  }

  static scrollToElement(id: string, context: Vue) {
    const element = context.$el.querySelector(`#${id}`) as HTMLElement;
    if (element) {
      context.$nextTick(() => {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      });
    }
  }

  static moveElement(elementToMove: string, parentElement: string) {
    const r = document.getElementById(elementToMove);
    if (r) document.getElementById(parentElement)?.appendChild(r);
  }

  static getRandomHexColorArray(n: number) {
    const result = new Array(n);
    const colorsQuantity = COLORS_PALETTE.length;
    while (n--) {
      result[n] = COLORS_PALETTE[n % colorsQuantity];
    }
    return result;
  }

  static watchClass(targetNode: HTMLElement, classToWatch: string) {
    let lastClassState = targetNode.classList.contains(classToWatch);
    const observer = new MutationObserver(mutationsList => {
      for (let i = 0; i < mutationsList.length; i++) {
        const mutation = mutationsList[i];
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const currentClassState = (mutation.target as HTMLElement).classList.contains(
            classToWatch
          );
          if (lastClassState !== currentClassState) {
            lastClassState = currentClassState;
            if (!currentClassState) {
              (mutation.target as HTMLElement).classList.add("sortHandle");
            }
          }
        }
      }
    });
    observer.observe(targetNode, { attributes: true });
  }

  static handleTableRowClass(el: Element) {
    el.querySelectorAll("th").forEach(draggableEl => {
      this.watchClass(draggableEl, "sortHandle");
      draggableEl.classList.add("sortHandle");
    });
  }

  static setBackgroundColors(chartData: ChartData) {
    chartData.datasets?.forEach(data => {
      let colors = [];
      const count = (data.data as number[]).length;
      if (count && count > 0) {
        colors = Utils.getRandomHexColorArray(count);
      }
      data.backgroundColor = colors;
      data.borderColor = "#fff";
    });
  }

  static createXLSXDocumentFromTableData(
    tableHeaders: TableHeader[],
    tableFields: TableField[]
  ) {
    const headerXLSXData: string[] = [];
    const bodyXLSXData: string[][] = Utils.getEmptyBody(tableFields.length);

    const workBook = XLSX.utils.book_new();

    workBook.SheetNames.push(EXCEL_CONSTANTS.SHEET_NAME);

    for (let i = 0; i < tableHeaders.length; i++) {
      const fieldName = tableHeaders[i].value as keyof TableField;
      headerXLSXData.push(tableHeaders[i].text);
      for (let j = 0; j < tableFields.length; j++) {
        const value = getKeyValue<keyof TableField, TableField>(fieldName)(
          tableFields[j]
        );
        if (typeof value === OBJECT) {
          const valueText = value ? JSON.stringify(value) : "";
          bodyXLSXData[j].push(valueText);
        } else {
          bodyXLSXData[j].push(value as string);
        }
      }
    }
    const workSheet = XLSX.utils.aoa_to_sheet(
      [headerXLSXData].concat(bodyXLSXData)
    );
    workBook.Sheets[EXCEL_CONSTANTS.SHEET_NAME] = workSheet;

    const wbout = XLSX.write(workBook, { bookType: "xlsx", type: "binary" });
    const buffer = new ArrayBuffer(wbout.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xff;
    return buffer;
  }

  static getFileName(componentName: string, mainfilter: string): string {
    const date = Date.now();
    return `${componentName}_${mainfilter}_${date}.xlsx`;
  }

  static getEmptyBody(length: number): string[][] {
    const emptyarray: string[][] = [];

    for (let i = 0; i < length; i++) {
      const row: string[] = [];
      emptyarray.push(row);
    }

    return emptyarray;
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  static findItemByRef(ref: string, item: any, actual = 0): any {
    const refsArray: string[] = ref.split(".");
    const actualRef: string = refsArray[actual];
    if (
      (!Array.isArray(item) &&
        typeof item !== KEYTYPE_CONSTANTS.OBJECT &&
        typeof actualRef === KEYTYPE_CONSTANTS.UNDEFINED) ||
      item === null ||
      actual === refsArray.length
    ) {
      return item;
    } else if (Object.prototype.hasOwnProperty.call(item, actualRef)) {
      return this.findItemByRef(ref, item[actualRef], ++actual);
    } else {
      if (item === Object(item)) {
        for (const i in item) {
          const refitem = this.findItemByRef(ref, item[i], actual);
          if (refitem !== null) {
            return refitem;
          }
        }
      }
      return null;
    }
  }
  static getUserHomePath() {
    let path = "";
    path = ROUTES.HOME;
    return path;
  }

  static mapChartData(data: CrudChartData, chartName: string): ChartRenderData {
    return this.mapChartDataByOne(data[chartName]);
  }

  static mapChartDataByOne(data: CrudChartOneData): ChartRenderData {
    return {
      labels: Object.keys(data),
      datasets: [
        {
          data: Object.values(data)
        }
      ]
    };
  }

  static formatDateFromNumber(date: number) {
    const dateObj = new Date(date);
    const MIDDLE_DAY = 12;
    let hour = dateObj.getHours().toString();
    if (dateObj.getHours() > 12)
      hour = `${(dateObj.getHours() + MIDDLE_DAY) % 12}`;
    const minutes = `${dateObj.getMinutes() || ""}`;
    const time = dateObj.getHours() >= MIDDLE_DAY ? "p. m." : "a. m.";
    return `${hour}${minutes.length === 0 ? "" : ":"}${minutes} ${time}`;
  }

  static listToDynamicSelectData(
    list: ListHeader[],
    typeOfList: string
  ): DynamicSelectData[] {
    if (list.length == 0) {
      return [];
    }
    switch (typeOfList) {
      case NAME_CLASS.LISTHEADER:
        return list.map(
          (x): DynamicSelectData => ({ id: x.idReference, name: x.name })
        );
    }
    return [];
  }

  static dynamicSelectDataToList(
    list: DynamicSelectData[],
    typeOfList: string
  ): ListHeader[] {
    switch (typeOfList) {
      case NAME_CLASS.LISTHEADER:
        return list.map(
          (x): ListHeader => ({ idReference: x.id, name: x.name })
        );
    }
    return [];
  }

  static changeActiveModalMessage(
    active: boolean,
    componentName: string,
    documentName?: string
  ) {
    let data: ModalFormData;
    if (active) {
      data = FORM_MESSAGES.DESACTIVED_MODAL;
    } else {
      data = FORM_MESSAGES.ACTIVED_MODAL;
    }
    const name = `${componentName} ${documentName}`;
    data.TEXT = data.TEXT.replace(FORM_MESSAGES.COMPONENT, name);

    return data;
  }

  static verifyIfHasAccess(componentName: string): AccessOptions {
    const access: Array<string> = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE.ACCESS) ?? "[]"
    );
    const options: AccessOptions = new AccessOptions();

    access
      .filter(option => option.includes(componentName))
      .forEach(option => {
        switch (option) {
          case `${ENDPOINT_OPTIONS.ALL}${componentName}`:
            options.all = true;
            break;
          case `${ENDPOINT_OPTIONS.PUT}${componentName}`:
            options.put = true;
            break;
          case `${ENDPOINT_OPTIONS.GET}${componentName}`:
            options.get = true;
            break;
          case `${ENDPOINT_OPTIONS.DELETE}${componentName}`:
            options.delete = true;
            break;
          case `${ENDPOINT_OPTIONS.POST}${componentName}`:
            options.post = true;
            break;
        }
      });

    return options;
  }

  static addExtraOperations(
    operations: string[],
    componentName: string,
    accessOperations = ACCESS_OPTIONS_TRUE
  ): string[] {
    const access = this.verifyIfHasAccess(componentName);

    if (accessOperations.put && (access.all || access.put)) {
      operations = [...operations, LIST_DATA_EVENTS.ACTIVE_DESACTIVED_DATA];
    }
    if (accessOperations.delete && (access.all || access.delete)) {
      operations = [...operations, LIST_DATA_EVENTS.DELETE_DATA];
    }
    if (accessOperations.post && (access.all || access.post)) {
      operations = [...operations, LIST_DATA_EVENTS.SAVE_DATA];
    }
    return operations;
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  static sendDataToLateralMenu(action: string, data?: any) {
    store.commit(STORE_CONSTANTS.TO_LATERAL_MENU, {
      from: PANEL_INFO_CONSTANTS.FROM.CRUD,
      type: PANEL_INFO_CONSTANTS.TYPE.SEARCH,
      action: action,
      data: data
    });
  }

  static calculeNewRoute(
    card: DashboardCard | RouteMapData,
    validateAux = true
  ): RouteLocation | undefined {
    let newRoute: RouteLocation | undefined;
    let pathName = card.pathName;
    if (validateAux && !pathName && card.pathNameAux)
      pathName = card.pathNameAux;

    if (pathName) {
      newRoute = {
        name: ""
      };
      newRoute.name = pathName;
      if (card.urlUser) {
        newRoute.params = { id: store.state.userId };
      }
    }
    if (card.openLateralMenu) {
      this.sendDataToLateralMenu(
        PANEL_INFO_CONSTANTS.ACTION.OPEN_LATERAL_PANEL,
        card.openLateralMenu
      );
    }
    return newRoute;
  }

  static numberTask(listTasks: FormsData[]): number {
    const taskNumber = listTasks.filter(form => {
      if (!form.form || form.form.status == FORMS_CONSTANTS.STATE.INCOMPLETE) {
        return true;
      }
    }).length;

    return taskNumber;
  }

  static dateToyyyymmdd(dateStrint: string) {
    const date = new Date(dateStrint as string);
    if (isNaN(+date)) {
      return dateStrint;
    }
    date.setSeconds(date.getSeconds() + 90);
    const mm = date.getMonth() + 1;
    const dd = date.getDate();

    return [
      date.getFullYear(),
      (mm > 9 ? "" : "0") + mm,
      (dd > 9 ? "" : "0") + dd
    ].join("/");
  }

  static async getListAndMapItAsSecondaryFilterOptions(
    id: string
  ): Promise<Array<SecondaryFilterOptions>> {
    try {
      const listService = new ListService();
      const response = await listService.getListById(id);

      if (response.code === Http.Found && response.data) {
        return response.data.options
          .filter(item => item.enabled)
          .map(item => {
            return { key: item.title, name: item.title };
          });
      } else {
        return DEFAULT_CATEGORY_SERVICE_OPTION;
      }
    } catch (error) {
      return DEFAULT_CATEGORY_SERVICE_OPTION;
    }
  }

  static initialModel(schema: FormSchema) {
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const model: any = {};
    schema.allOf.forEach((item: FieldSchema) => {
      for (const key in item.properties) {
        if (item.properties[key].type === KEYTYPE_CONSTANTS.BOOLEAN) {
          model[key] = false;
        }
      }
    });
    return model;
  }

  static getClientStoragePath() {
    return `${FILE.APPLICATION_STORAGE_FOLDER}/${localStorage.getItem(
      LOCAL_STORAGE.CLIENT_ID
    )}`;
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  static searchItem(
    list: any,
    keyWordParam: string,
    property: string,
    setterNoData: CallableFunction
  ) {
    const keyWord = keyWordParam?.toLowerCase() ?? "";
    if (
      keyWord &&
      !list.find((item: any) => item[property].toLowerCase() === keyWord)
    ) {
      setterNoData(true);
      return;
    }
    setterNoData(false);
  }

  static isMultipleOption(
    nameFilter: string,
    allSelectFilter: AllObjFilterList,
    allItemFilter: SecondaryFilter[]
  ): boolean {
    const filterBase = allItemFilter.find(
      filterSelect => filterSelect.key == nameFilter
    );
    if (filterBase?.multipleOff) {
      return false;
    }
    for (const nameFilterSelect of Object.keys(allSelectFilter)) {
      const filterSelect = allItemFilter.find(
        filterSelect => filterSelect.key == nameFilterSelect
      );
      const filtern = allSelectFilter[nameFilterSelect];
      if (
        nameFilterSelect != nameFilter &&
        (FILTER_DISPLAY.LIST === filterSelect?.display ||
          FILTER_DISPLAY.INPUT === filterSelect?.display) &&
        filtern &&
        filtern.length > 1
      ) {
        return false;
      }
    }
    return true;
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  static changeStateList(thisObject: any, ref: string, newState: boolean) {
    thisObject.$nextTick(() => {
      if (thisObject.$refs[ref]) {
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        (thisObject.$refs[ref] as any).isMenuActive = newState;
      }
    });
  }

  static getMethodHTTP(error: AxiosError): string {
    const method = error.response?.config.method || "get";
    return METHODS[method as keyof typeof METHODS];
  }

  static async checkUniqueName(
    collection: string,
    value: string,
    extraInfo?: string
  ) {
    const errors: string[] = [];
    if (value.trim() == "") {
      return errors;
    }

    let refService;
    let response;
    switch (collection) {
      case COLLECTIONS_NAME.ACTION_PLAN:
        refService = new ActionPlanService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.CAMPUS:
        refService = new CampusService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.CUPS_PRICE_LEVEL:
        refService = new CupsPriceLevelService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.DEPENDENCY:
        refService = new DependenciesService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.FORM_TEMPLATE:
        refService = new FormTemplateService();
        response = await refService?.checkIfFormTemplateExist(value);
        break;
      case COLLECTIONS_NAME.RULE:
        refService = new GeneralRulesService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.INFO_GROUP:
        refService = new InfoGroupService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.LIST:
        refService = new ListService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.MULTIMEDIA:
        refService = new MultimediaService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.ROLE:
        refService = new RolesService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.SERVICE_TEMPLATE:
        refService = new ServiceTemplateService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.SESSION_TEMPLATE:
        refService = new SessionTemplateService();
        response = await refService?.checkIfSessionNameExists(value);
        break;
      case COLLECTIONS_NAME.SPACE:
        refService = new SpaceService();
        response = await refService?.checkIfExistByName(value, extraInfo);
        break;
      case COLLECTIONS_NAME.TEMPLATES_PDF:
        refService = new TemplatePdfService();
        response = await refService?.checkIfExistByName(value);
        break;
      case COLLECTIONS_NAME.USER_TEMPLATE:
        refService = new UserTemplateService();
        response = await refService?.checkIfExistByName(value);
        break;
    }

    if (response && response.code == Http.Found) {
      errors.push(GENERAL_CONSTANTS.MESSAGE_NAME_USED);
    }
    return errors;
  }

  static detectScroll(
    references: {
      [key: string]: Element | Vue | Vue[] | Element[];
    },
    nameRef: string
  ) {
    const refSelect = references[nameRef] as any;
    const refVue = Array.isArray(refSelect) ? refSelect[0] : refSelect;
    if (!refVue || !refVue.detectScroll) {
      return;
    }
    refVue.detectScroll();
  }

  static idGenerator() {
    const caracteres = ALPHANUMERIC_CHARACTERS;
    let id = "";
    for (let i = 0; i < 6; i++) {
      id += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
    }
    return id;
  }

  static changeColorOpacity(hexColor: string, opacity: string): string {
    return `${hexColor.slice(0, -2)}${opacity}`;
  }

  static async signUrl(
    documentService: DocumentService,
    defaultDocument: DocumentDTO,
    userId: string
  ) {
    try {
      const signedUrlResponse = await documentService.signUrl({
        storagePath: defaultDocument.documentPath as string,
        documentId: defaultDocument._key as string,
        userId: userId as string
      });
      if (signedUrlResponse.code === Http.Ok) {
        return signedUrlResponse.data;
      }
      Snackbar.popError(signedUrlResponse.message);
      return false;
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + DASHBOARD_ACCESS.POST
        );
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
      return false;
    }
  }

  static async shortUrl(
    documentService: DocumentService,
    defaultDocument: DocumentDTO,
    userId: string
  ) {
    try {
      const signedUrlResponse = await documentService.shortUrl({
        storagePath: defaultDocument.documentPath as string,
        documentId: defaultDocument._key as string,
        userId: userId as string
      });

      if (signedUrlResponse.code === Http.Ok) {
        return signedUrlResponse.data;
      }
      Snackbar.popError(signedUrlResponse.message);
      return "";
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + DASHBOARD_ACCESS.POST
        );
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
      return "";
    }
  }

  static async getUIConfig() {
    // await this.exportUiConfig()
    const serviceUrl = `${process.env.VUE_APP_FORMS_API}/ui-config/get-info`;
    const response = await axios.get(serviceUrl);
    return response.data.data;
  }

  static async exportUiConfig() {
    const serviceUrl = `${process.env.VUE_APP_FORMS_API}/ui-config/export`;
    const response = await axios.post(serviceUrl);
    return response.data;
  }

  static generateColorsTones(color: string, totalColors: number): string[] {
    const base = tinycolor(color);
    const colors: string[] = [];

    if (totalColors <= 1) return [base.toHexString()];

    // margen para no llegar nunca a blanco/negro
    const lightenMax = 89;
    const darkenMax = 89;

    const { l } = base.toHsl();
    const spaceLight = 100 - l * 100; // % hasta blanco

    const step = 89 / (totalColors + 1); // %
    const maxStepsLight = Math.floor(spaceLight / step);

    const baseIndex = Math.min(Math.floor(totalColors / 2), maxStepsLight);

    colors[baseIndex] = base.toHexString();

    for (let i = 1; i <= baseIndex; i++) {
      const ratio = (i / (baseIndex + 1)) * lightenMax;
      colors[baseIndex - i] = tinycolor.mix(base, "white", ratio).toHexString();
    }

    for (let i = 1; i <= totalColors - baseIndex - 1; i++) {
      const ratio = (i / (totalColors - baseIndex)) * darkenMax;
      colors[baseIndex + i] = tinycolor.mix(base, "black", ratio).toHexString();
    }
    return colors;
  }

  private static isHtmlFieldDefinition(fieldDef: unknown): boolean {
    if (!fieldDef || typeof fieldDef !== "object") {
      return false;
    }
    const d = fieldDef as Record<string, unknown>;
    return d["x-display"] === "html" || d.htmlContent !== undefined;
  }

  private static filterSchemaPropertiesForHtmlSplit(
    props: Record<string, unknown> | undefined,
    htmlOnly: boolean
  ): Record<string, unknown> | undefined {
    if (!props) {
      return undefined;
    }
    const next: Record<string, unknown> = {};
    Object.entries(props).forEach(([key, def]) => {
      const isHtml = Utils.isHtmlFieldDefinition(def);
      if (htmlOnly ? isHtml : !isHtml) {
        next[key] = def;
      }
    });
    return Object.keys(next).length ? next : undefined;
  }

  private static filterAllOfEntryForHtmlSplit(
    block: Record<string, unknown>,
    htmlOnly: boolean
  ): Record<string, unknown> | null {
    const filtered = Utils.filterSchemaPropertiesForHtmlSplit(
      block.properties as Record<string, unknown> | undefined,
      htmlOnly
    );
    if (!filtered || Object.keys(filtered).length === 0) {
      return null;
    }
    const allowedKeys = new Set(Object.keys(filtered));
    const out: Record<string, unknown> = { ...block, properties: filtered };
    const req = block.required as string[] | undefined;
    if (req?.length) {
      const nr = req.filter(k => allowedKeys.has(k));
      if (nr.length) {
        out.required = nr;
      } else {
        delete out.required;
      }
    }
    return out;
  }

  /** Recoge las claves de campo definidas como HTML en el schema JSON (properties + allOf). */
  static collectHtmlFieldKeysFromSchema(
    schema: JsonSchemaFragmentLike
  ): Set<string> {
    const keys = new Set<string>();
    if (schema.properties) {
      Object.entries(schema.properties).forEach(([key, def]) => {
        if (Utils.isHtmlFieldDefinition(def)) {
          keys.add(key);
        }
      });
    }
    schema.allOf?.forEach(block => {
      if (!block.properties) {
        return;
      }
      Object.entries(block.properties).forEach(([key, def]) => {
        if (Utils.isHtmlFieldDefinition(def)) {
          keys.add(key);
        }
      });
    });
    return keys;
  }

  /** Todas las claves de campo declaradas en `properties` y `allOf[].properties`. */
  static collectFieldKeysFromSchema(
    schema: JsonSchemaFragmentLike
  ): Set<string> {
    const keys = new Set<string>();
    if (schema.properties) {
      Object.keys(schema.properties).forEach(k => keys.add(k));
    }
    schema.allOf?.forEach(block => {
      if (block.properties) {
        Object.keys(block.properties).forEach(k => keys.add(k));
      }
    });
    return keys;
  }

  /**
   * Divide el schema en dos fragmentos: solo campos HTML (x-display html / htmlContent) vs resto (v-jsf).
   * Conserva metadatos de nivel superior del objeto schema (p. ej. fields, conditions, readOnly).
   */
  static splitFormSchemaByHtmlDisplay(
    schema: JsonSchemaFragmentLike
  ): SplitFormSchemaByHtmlResult {
    const src = schema as Record<string, unknown>;
    const htmlProps = Utils.filterSchemaPropertiesForHtmlSplit(
      schema.properties as Record<string, unknown> | undefined,
      true
    );
    const vjsfProps = Utils.filterSchemaPropertiesForHtmlSplit(
      schema.properties as Record<string, unknown> | undefined,
      false
    );

    const htmlAllOf: Record<string, unknown>[] = [];
    const vjsfAllOf: Record<string, unknown>[] = [];

    for (const block of schema.allOf ?? []) {
      const br = block as Record<string, unknown>;
      const h = Utils.filterAllOfEntryForHtmlSplit(br, true);
      const v = Utils.filterAllOfEntryForHtmlSplit(br, false);
      if (h) {
        htmlAllOf.push(h);
      }
      if (v) {
        vjsfAllOf.push(v);
      }
    }

    const htmlSchema: JsonSchemaFragmentLike = {
      ...(src as object),
      type: schema.type ?? "object",
      properties: htmlProps,
      allOf: htmlAllOf as JsonSchemaFragmentLike["allOf"]
    };
    const vjsfSchema: JsonSchemaFragmentLike = {
      ...(src as object),
      type: schema.type ?? "object",
      properties: vjsfProps,
      allOf: vjsfAllOf as JsonSchemaFragmentLike["allOf"]
    };
    return { htmlSchema, vjsfSchema };
  }

  /**
   * Reparte valores guardados según las claves del fragmento HTML del schema ya separado.
   * Recibe el resultado de `splitFormSchemaByHtmlDisplay`.
   */
  static splitFormFieldsFromSchemas(
    fields: Record<string, unknown> | undefined | null,
    separatedSchema: SplitFormSchemaByHtmlResult
  ): SplitFormFieldsByHtmlResult {
    const flat = { ...(fields ?? {}) };
    const htmlKeys = Utils.collectFieldKeysFromSchema(
      separatedSchema.htmlSchema
    );
    const htmlFields: Record<string, unknown> = {};
    const vjsfFields: Record<string, unknown> = {};

    Object.entries(flat).forEach(([key, value]) => {
      if (htmlKeys.has(key)) {
        htmlFields[key] = value;
      } else {
        vjsfFields[key] = value;
      }
    });

    return { htmlFields, vjsfFields };
  }

  /**
   * Separa `form.fields` usando el schema completo (equivale a partir schema + model).
   */
  static splitFormFieldsByHtmlDisplay(
    fields: Record<string, unknown> | undefined | null,
    schema: JsonSchemaFragmentLike
  ): SplitFormFieldsByHtmlResult {
    const separated = Utils.splitFormSchemaByHtmlDisplay(schema);
    return Utils.splitFormFieldsFromSchemas(fields, separated);
  }
}
